/**
 * MBTIQuestionCard
 *
 * Renders a single MBTI psychometric question using a bipolar 1-5 scale
 * with a visible track line connecting the circles to convey "spectrum".
 * Auto-advances to next question after selection with a short delay.
 */

import { PsychometricQuestionData } from '../../types/interview.types';

import type { Locale } from '@/i18n/config';

interface MBTIQuestionCardProps {
  question: PsychometricQuestionData;
  answer: number | undefined;
  onAnswer: (value: number) => void;
  /** Exam content locale — scale labels follow exam language, not UI locale. */
  contentLocale?: Locale;
}

const CIRCLE_LABELS_EN: Record<number, string> = {
  1: 'Strongly',
  2: 'Slightly',
  3: 'Neutral',
  4: 'Slightly',
  5: 'Strongly',
};

const CIRCLE_LABELS_FR: Record<number, string> = {
  1: 'Fortement',
  2: 'Légèrement',
  3: 'Neutre',
  4: 'Légèrement',
  5: 'Fortement',
};

function isLikelyFrench(text: string): boolean {
  return /(d['’]accord|pas du tout|tout à fait|fortement|légèrement|neutre|à|é|è|ê|ç|œ)/i.test(text);
}

export function MBTIQuestionCard({
  question,
  answer,
  onAnswer,
  contentLocale,
}: MBTIQuestionCardProps) {
  const options = [...question.options].sort((a, b) => a.value - b.value);
  const locale: Locale =
    contentLocale ??
    (isLikelyFrench(`${question.text} ${question.left_text} ${question.right_text}`)
      ? 'fr'
      : 'en');
  const circleLabels = locale === 'fr' ? CIRCLE_LABELS_FR : CIRCLE_LABELS_EN;

  return (
    <div className="flex flex-col items-center w-full px-4 py-6 select-none">
      {/* Question text */}
      <div className="w-full max-w-2xl mb-8">
        <p className="text-lg text-dark-invirtus font-body text-center leading-relaxed">
          {question.text}
        </p>
      </div>

      {/* Bipolar scale */}
      <fieldset
        className="w-full max-w-md"
        aria-label={question.text}
      >
        <legend className="sr-only">
          Rate from {question.left_text} (1) to {question.right_text} (5)
        </legend>

        {/* Pole labels — above the circles, full width */}
        <div className="flex items-start justify-between gap-6 mb-3">
          <span className="text-sm font-semibold text-dark-invirtus font-heading text-left leading-tight max-w-[45%]">
            {question.left_text}
          </span>
          <span className="text-sm font-semibold text-dark-invirtus font-heading text-right leading-tight max-w-[45%]">
            {question.right_text}
          </span>
        </div>

        {/* Track + circles */}
        <div className="relative flex items-center justify-between py-2">
          {/* Track line through circle centers */}
          <div className="absolute h-0.5 bg-gray-300 rounded-full" style={{ left: '10%', right: '10%', top: 'calc(50% - 8px)' }} />

          {/* Radio circles */}
          {options.map((option) => {
            const isSelected = answer === option.value;
            const label = circleLabels[option.value];
            // Sizes: 1,5 = large, 2,4 = medium, 3 = small
            const sizeClass =
              option.value === 1 || option.value === 5
                ? 'w-12 h-12 sm:w-14 sm:h-14 text-sm'
                : option.value === 3
                ? 'w-8 h-8 sm:w-9 sm:h-9 text-xs'
                : 'w-10 h-10 sm:w-11 sm:h-11 text-xs';

            return (
              <label
                key={option.id}
                className="relative z-10 flex flex-col items-center cursor-pointer group flex-1"
              >
                <input
                  type="radio"
                  name={`mbti-question-${question.id}`}
                  value={option.value}
                  checked={isSelected}
                  onChange={() => onAnswer(option.value)}
                  className="sr-only"
                />
                <button
                  type="button"
                  onClick={() => onAnswer(option.value)}
                  aria-pressed={isSelected}
                  className={[
                    sizeClass,
                    'rounded-full border-2 flex items-center justify-center',
                    'font-bold font-heading transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple',
                    isSelected
                      ? 'bg-purple border-purple text-white shadow-lg scale-110'
                      : 'bg-white border-gray-300 text-gray-500 hover:border-purple hover:text-purple group-hover:scale-105',
                  ].join(' ')}
                >
                  {option.value}
                </button>
                <span className="text-[10px] text-gray-400 font-body mt-1.5 leading-none">
                  {label}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}
